import { useState, useMemo } from 'react'
import { Flex, Form, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { FormattedMessage } from 'react-intl'
import Text from 'shared/components/typography/Text'
import CryptoSelect from '../../CryptoSelect'
import { formKeys } from '../../DonationForm/constants'
import TypeBlock from '../TypeBlock'
import Option from '../TypeBlock/Option'
import { getVisibleOptions } from '../utils'
import ShowAllButton from '../ShowAllButton'
import { lang } from './lang'
import { checkOptionSelected, getCryptoAssetWithNetwork, getQuickCryptoOptions } from './utils'
import { CryptoAsset, FormValues } from '@/pages/Donation/types'
import { PaymentType } from '@/pages/Donation/api/streamer/types'
import { ICryptoCurrency } from '@/pages/Donation/api/types'

type Props = {
  currencies: ICryptoCurrency[]
  isOptionsLoading: boolean
  onSelectCryptoAsset: (value: ICryptoCurrency | null, networkCode?: string) => void
}

export default function Crypto({ currencies, onSelectCryptoAsset, isOptionsLoading }: Props) {
  const form = Form.useFormInstance<FormValues>()
  const [isOptionsModalOpen, setOptionsModalOpen] = useState(false)
  const paymentType = Form.useWatch<FormValues[typeof formKeys.paymentType]>(formKeys.paymentType)
  const selectedCryptoAsset = Form.useWatch<FormValues[typeof formKeys.selectedCryptoAsset]>(
    formKeys.selectedCryptoAsset
  )
  const selectedNetwork = Form.useWatch<FormValues[typeof formKeys.selectedNetwork]>(formKeys.selectedNetwork)

  const handleOptionsModalOpen = () => setOptionsModalOpen(true)
  const handleOptionsModalClose = () => setOptionsModalOpen(false)

  const handleSelectQuickOption = (option: CryptoAsset) => {
    const currency = currencies.find(c => c.code === option.code) ?? null
    onSelectCryptoAsset(currency, option.network?.code)
  }

  const selectedAsset = getCryptoAssetWithNetwork(selectedCryptoAsset, selectedNetwork?.code)
  const quickOptions = useMemo(() => getQuickCryptoOptions(currencies), [currencies])
  const visibleOptions = getVisibleOptions(quickOptions, selectedAsset)

  let rightContent = null

  if (!currencies?.length && isOptionsLoading) {
    rightContent = <Spin indicator={<LoadingOutlined style={{ color: 'var(--accent-light)' }} />} />
  } else if (paymentType === PaymentType.Crypto) {
    rightContent = <ShowAllButton onClick={handleOptionsModalOpen} />
  } else {
    rightContent = (
      <Flex gap={8}>
        {visibleOptions.map(option => (
          <img key={option.code} src={option.icon} style={{ width: '20px' }} />
        ))}
      </Flex>
    )
  }

  return (
    <>
      <CryptoSelect
        isOpen={isOptionsModalOpen}
        onClose={handleOptionsModalClose}
        options={currencies}
        selectedCurrency={selectedCryptoAsset}
        selectedNetwork={selectedNetwork}
        onSelectCurrency={onSelectCryptoAsset}
        onSelectNetwork={value => form.setFieldsValue({ [formKeys.selectedNetwork]: value })}
      />
      <Form.Item name={formKeys.selectedCryptoAsset} noStyle />
      <Form.Item name={formKeys.selectedNetwork} noStyle />
      <TypeBlock<CryptoAsset>
        isSelected={paymentType === PaymentType.Crypto}
        paymentType={PaymentType.Crypto}
        visibleOptions={visibleOptions}
        title={<FormattedMessage {...lang.crypto} />}
        rightContent={rightContent}
        renderOption={option => {
          const { icon, code, name, network } = option
          const showNetwork = network?.name && network?.protocol

          return (
            <Option
              icon={icon}
              code={code}
              onClick={() => handleSelectQuickOption(option)}
              isSelected={checkOptionSelected(option, selectedAsset)}
            >
              <Flex align="center" gap={6}>
                <Flex align="center" gap={4} style={{ maxWidth: 274 }}>
                  <Text ellipsis type="primary" fontSize={14} fontType="body">
                    {code}
                  </Text>
                  <Text ellipsis fontType="description" color="var(--grey-500)">
                    {name}
                  </Text>
                  {showNetwork && (
                    <>
                      <Text ellipsis type="primary" fontSize={14} fontType="body">
                        {network?.name}
                      </Text>
                      <Text ellipsis fontType="description" color="var(--grey-500)">
                        {network?.protocol}
                      </Text>
                    </>
                  )}
                </Flex>
              </Flex>
            </Option>
          )
        }}
      />
    </>
  )
}
